import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Package, LogOut, Plus, X, BookOpen, Pencil, Save, Trash2,
  Star, Search, Filter, Upload, Calendar, ChevronLeft, ChevronRight,
  FileText, Link2, Eye, RefreshCw, FileUp, Check, ArrowRight, AlertTriangle
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const SPEED_INDICES = ["J","K","L","M","N","P","Q","R","S","T","U","H","V","W","Y"];
const STATUS_CONFIG = {
  en_stock: { label: "En stock", color: "bg-[#16A34A]", text: "text-white", dot: "bg-[#16A34A]" },
  en_vente: { label: "En vente", color: "bg-[#EAB308]", text: "text-[#0A0A0A]", dot: "bg-[#EAB308]" },
  vendu:    { label: "Vendu",    color: "bg-[#DC2626]", text: "text-white", dot: "bg-[#DC2626]" },
};
const WIDTHS = [135,145,155,165,175,185,195,205,215,225,235,245,255,265,275,285,295,305,315];
const HEIGHTS = [25,30,35,40,45,50,55,60,65,70,75,80];
const DIAMETERS = [13,14,15,16,17,18,19,20,21,22];
const PAYMENT_METHODS = [
  { value: "especes", label: "Espèces" },
  { value: "virement", label: "Virement" },
  { value: "cb", label: "CB" },
  { value: "autre", label: "Autre" },
];
const MONTH_NAMES = ["","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.en_stock;
  return <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold uppercase tracking-wide rounded-sm ${cfg.color} ${cfg.text}`} data-testid={`status-badge-${status}`}>{cfg.label}</span>;
};

const ConditionStars = ({ score }) => {
  if (!score) return null;
  return (
    <span className="inline-flex items-center text-[#0A0A0A]">
      <span className="sm:hidden text-xs font-bold">{score}</span>
      <span className="hidden sm:inline-flex items-center">
        {[1,2,3,4].map(i => <Star key={i} className={`w-3 h-3 ${i <= score ? 'fill-current' : 'opacity-20'}`} />)}
      </span>
    </span>
  );
};

// ==========================================
// Compta Tire Form (Add/Edit) — shared
// ==========================================
const ComptaTireForm = ({ tire, onClose, onSuccess }) => {
  const isEdit = !!tire;
  const [formData, setFormData] = useState({
    width: tire?.width || "", height: tire?.height || "", diameter: tire?.diameter || "",
    load_index: tire?.load_index || "", speed_index: tire?.speed_index || "",
    brand: tire?.brand || "", model: tire?.model || "",
    tire_type: tire?.tire_type || "used", season: tire?.season || "ete",
    runflat: tire?.runflat || false, camionnette: tire?.camionnette || false,
    nv: tire?.nv || false, condition_score: tire?.condition_score || "",
    dot: tire?.dot || "", quantity: tire?.quantity || 1,
    is_tested: tire?.is_tested !== undefined ? tire.is_tested : true,
    purchase_value: tire?.purchase_value ?? "", resale_value: tire?.resale_value ?? "",
    status: tire?.status || "en_stock",
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.width || !formData.height || !formData.diameter || !formData.brand || !formData.model) {
      toast.error("Veuillez remplir les champs obligatoires"); return;
    }
    setIsSaving(true);
    try {
      const payload = {
        width: parseInt(formData.width), height: parseInt(formData.height), diameter: parseInt(formData.diameter),
        load_index: formData.load_index ? parseInt(formData.load_index) : null,
        speed_index: formData.speed_index || null, brand: formData.brand, model: formData.model,
        tire_type: formData.tire_type, season: formData.season,
        runflat: formData.runflat, camionnette: formData.camionnette, nv: formData.nv,
        condition_score: formData.condition_score ? parseInt(formData.condition_score) : null,
        dot: formData.dot || null, quantity: parseInt(formData.quantity) || 1, is_tested: formData.is_tested,
        purchase_value: formData.purchase_value !== "" ? parseFloat(formData.purchase_value) : null,
        resale_value: formData.resale_value !== "" ? parseFloat(formData.resale_value) : null,
        status: formData.status,
      };
      if (isEdit) {
        await axios.put(`${API_URL}/api/compta/tires/${tire.id}`, payload, { withCredentials: true });
        toast.success("Pneu modifié");
      } else {
        await axios.post(`${API_URL}/api/compta/tires`, payload, { withCredentials: true });
        toast.success("Pneu ajouté à Compta Stock");
      }
      onSuccess(); onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setIsSaving(false); }
  };

  const Field = ({ label, children, className = "" }) => (
    <div className={className}><label className="input-label">{label}</label>{children}</div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="compta-tire-form">
      <div className="grid grid-cols-3 gap-3">
        <Field label="Largeur *"><Select value={formData.width.toString()} onValueChange={v => setFormData(p => ({...p, width: v}))}><SelectTrigger className="input-field"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{WIDTHS.map(w => <SelectItem key={w} value={w.toString()}>{w}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Hauteur *"><Select value={formData.height.toString()} onValueChange={v => setFormData(p => ({...p, height: v}))}><SelectTrigger className="input-field"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{HEIGHTS.map(h => <SelectItem key={h} value={h.toString()}>{h}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Diamètre *"><Select value={formData.diameter.toString()} onValueChange={v => setFormData(p => ({...p, diameter: v}))}><SelectTrigger className="input-field"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{DIAMETERS.map(d => <SelectItem key={d} value={d.toString()}>{d}</SelectItem>)}</SelectContent></Select></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Indice charge"><Input className="input-field" type="number" value={formData.load_index} onChange={e => setFormData(p => ({...p, load_index: e.target.value}))} placeholder="91" /></Field>
        <Field label="Indice vitesse"><Select value={formData.speed_index} onValueChange={v => setFormData(p => ({...p, speed_index: v}))}><SelectTrigger className="input-field"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{SPEED_INDICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Marque *"><Input className="input-field" value={formData.brand} onChange={e => setFormData(p => ({...p, brand: e.target.value}))} placeholder="Michelin" /></Field>
        <Field label="Modèle *"><Input className="input-field" value={formData.model} onChange={e => setFormData(p => ({...p, model: e.target.value}))} placeholder="Primacy 4" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Type"><Select value={formData.tire_type} onValueChange={v => setFormData(p => ({...p, tire_type: v}))}><SelectTrigger className="input-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">Neuf</SelectItem><SelectItem value="used">Occasion</SelectItem></SelectContent></Select></Field>
        <Field label="Saison"><Select value={formData.season} onValueChange={v => setFormData(p => ({...p, season: v}))}><SelectTrigger className="input-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ete">Été</SelectItem><SelectItem value="hiver">Hiver</SelectItem><SelectItem value="4saisons">4 Saisons</SelectItem></SelectContent></Select></Field>
        <Field label="DOT"><Input className="input-field" value={formData.dot} onChange={e => setFormData(p => ({...p, dot: e.target.value}))} placeholder="2024" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Quantité"><Input className="input-field" type="number" min="1" value={formData.quantity} onChange={e => setFormData(p => ({...p, quantity: e.target.value}))} /></Field>
        <Field label="Usure (1-4)"><Select value={formData.condition_score ? formData.condition_score.toString() : ""} onValueChange={v => setFormData(p => ({...p, condition_score: v}))}><SelectTrigger className="input-field"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{[1,2,3,4].map(s => <SelectItem key={s} value={s.toString()}>{s} — {"★".repeat(s)}</SelectItem>)}</SelectContent></Select></Field>
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.runflat} onCheckedChange={v => setFormData(p => ({...p, runflat: v}))} />RunFlat</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.camionnette} onCheckedChange={v => setFormData(p => ({...p, camionnette: v}))} />Camionnette</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.nv} onCheckedChange={v => setFormData(p => ({...p, nv: v}))} />NV</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={formData.is_tested} onCheckedChange={v => setFormData(p => ({...p, is_tested: v}))} />Testé</label>
      </div>
      <div className="border-t border-[#E5E7EB] pt-4 mt-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#2563EB] mb-3">Données comptables</div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Valeur d'achat (€)"><Input className="input-field" type="number" step="0.01" min="0" value={formData.purchase_value} onChange={e => setFormData(p => ({...p, purchase_value: e.target.value}))} placeholder="0.00" /></Field>
          <Field label="Valeur de revente (€)"><Input className="input-field" type="number" step="0.01" min="0" value={formData.resale_value} onChange={e => setFormData(p => ({...p, resale_value: e.target.value}))} placeholder="0.00" /></Field>
          <Field label="Statut"><Select value={formData.status} onValueChange={v => setFormData(p => ({...p, status: v}))}><SelectTrigger className="input-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="en_stock">En stock</SelectItem><SelectItem value="en_vente">En vente</SelectItem><SelectItem value="vendu">Vendu</SelectItem></SelectContent></Select></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="h-9">Annuler</Button>
        <Button type="submit" className="btn-primary h-9" disabled={isSaving} data-testid="compta-form-submit">{isSaving ? "Enregistrement..." : isEdit ? "Modifier" : "Ajouter"}</Button>
      </div>
    </form>
  );
};

// ==========================================
// Compta Tire Row
// ==========================================
const ComptaTireRow = ({ group, onEdit, onDelete, onStatusChange, expandedKey, onExpand }) => {
  const tire = group.tires?.[0];
  if (!tire) return null;
  const isExpanded = expandedKey === group.group_key;
  return (
    <div className="border-b border-[#E5E7EB] last:border-b-0" data-testid={`compta-row-${tire.id}`}>
      <div className="flex items-center justify-between py-2 px-3 hover:bg-[#F3F4F6] cursor-pointer transition-colors text-sm" onClick={() => onExpand(isExpanded ? null : group.group_key)}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusBadge status={group.status} />
          <span className="font-mono font-bold text-[#0A0A0A] whitespace-nowrap text-xs sm:text-sm">
            <span className="sm:hidden">{group.dimensions.replace(/ /g, "/").replace(/R/, "R")}{group.load_speed ? ` ${group.load_speed}` : ''}</span>
            <span className="hidden sm:inline">{group.dimensions}{group.load_speed ? ` ${group.load_speed}` : ''}</span>
          </span>
          <span className="text-[#525252] truncate">{group.brand} {group.model}</span>
          <ConditionStars score={group.condition_score} />
          {group.nv && <span className="text-[10px] font-bold text-[#DC2626] border border-[#DC2626] rounded px-1 leading-tight">[NV]</span>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {group.purchase_value != null && <span className="text-xs text-[#525252] hidden sm:inline"><span className="text-[#A3A3A3]">Achat:</span> {group.purchase_value.toFixed(2)}€</span>}
          {group.resale_value != null && <span className="text-xs text-[#525252] hidden sm:inline"><span className="text-[#A3A3A3]">Revente:</span> {group.resale_value.toFixed(2)}€</span>}
          <span className="font-mono font-bold text-[#0A0A0A] w-6 text-right">{group.total_quantity}</span>
        </div>
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 bg-[#F9FAFB] border-t border-[#E5E7EB] animate-slide-up" data-testid={`compta-expanded-${tire.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
            <div><span className="text-[#A3A3A3]">Dimensions:</span> <span className="font-mono font-bold">{tire.width}/{tire.height}R{tire.diameter}</span></div>
            <div><span className="text-[#A3A3A3]">Charge/Vitesse:</span> <span className="font-mono font-bold">{tire.load_index || '—'}{tire.speed_index || ''}</span></div>
            <div><span className="text-[#A3A3A3]">Type:</span> {tire.tire_type === "new" ? "Neuf" : "Occasion"}</div>
            <div><span className="text-[#A3A3A3]">Saison:</span> {tire.season === "ete" ? "Été" : tire.season === "hiver" ? "Hiver" : "4 Saisons"}</div>
            <div><span className="text-[#A3A3A3]">DOT:</span> {tire.dot || "—"}</div>
            <div><span className="text-[#A3A3A3]">Valeur d'achat:</span> <span className="font-bold">{tire.purchase_value != null ? `${tire.purchase_value.toFixed(2)}€` : "—"}</span></div>
            <div><span className="text-[#A3A3A3]">Valeur de revente:</span> <span className="font-bold">{tire.resale_value != null ? `${tire.resale_value.toFixed(2)}€` : "—"}</span></div>
            <div><span className="text-[#A3A3A3]">Quantité:</span> {tire.quantity}</div>
            <div><span className="text-[#A3A3A3]">Statut:</span> <StatusBadge status={tire.status} /></div>
          </div>
          <div className="flex items-center gap-2">
            {tire.status !== "vendu" && (
              <Select value="" onValueChange={(v) => onStatusChange(tire.id, v)}>
                <SelectTrigger className="h-7 w-[140px] text-xs"><SelectValue placeholder="Changer statut" /></SelectTrigger>
                <SelectContent>
                  {tire.status !== "en_stock" && <SelectItem value="en_stock">En stock</SelectItem>}
                  {tire.status !== "en_vente" && <SelectItem value="en_vente">En vente</SelectItem>}
                  <SelectItem value="vendu">Vendu</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onEdit(tire)} data-testid={`compta-edit-${tire.id}`}><Pencil className="w-3 h-3 mr-1" /> Modifier</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs text-[#DC2626] hover:bg-[#FEF2F2]" onClick={() => onDelete(tire.id)} data-testid={`compta-delete-${tire.id}`}><Trash2 className="w-3 h-3 mr-1" /> Supprimer</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// TAB 2 — BILANS MENSUELS
// ==========================================
const BilansMensuels = () => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [comptaTires, setComptaTires] = useState([]);
  const [linkingTxn, setLinkingTxn] = useState(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/compta/monthly-summary`, {
        params: { month: selectedMonth.month, year: selectedMonth.year }, withCredentials: true,
      });
      setSummary(res.data);
    } catch (e) { toast.error("Erreur chargement bilan"); }
    finally { setIsLoading(false); }
  }, [selectedMonth]);

  const fetchComptaTires = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/compta/tires`, { withCredentials: true });
      setComptaTires(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchSummary(); fetchComptaTires(); }, [fetchSummary, fetchComptaTires]);

  const navigateMonth = (dir) => {
    setSelectedMonth(prev => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { month: m, year: y };
    });
  };

  const handleAddTxn = async (data) => {
    try {
      await axios.post(`${API_URL}/api/compta/transactions`, data, { withCredentials: true });
      toast.success("Transaction ajoutée");
      setShowAddTxn(false);
      fetchSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleUpdateTxn = async (id, data) => {
    try {
      await axios.put(`${API_URL}/api/compta/transactions/${id}`, data, { withCredentials: true });
      toast.success("Transaction modifiée");
      setEditingTxn(null);
      fetchSummary();
    } catch (e) { toast.error("Erreur modification"); }
  };

  const handleDeleteTxn = async (id) => {
    if (!window.confirm("Supprimer cette transaction ?")) return;
    try {
      await axios.delete(`${API_URL}/api/compta/transactions/${id}`, { withCredentials: true });
      toast.success("Transaction supprimée");
      fetchSummary();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const handleLinkTires = async (txnId, tireIds) => {
    try {
      await axios.put(`${API_URL}/api/compta/transactions/${txnId}`, { tire_ids: tireIds }, { withCredentials: true });
      toast.success("Pneus associés");
      setLinkingTxn(null);
      fetchSummary();
    } catch (e) { toast.error("Erreur association"); }
  };

  const handleImportStatement = async (file) => {
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/compta/analyze-statement`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResults(res.data);
      if (res.data.error) toast.error(res.data.error);
      else toast.success(`${res.data.transactions?.length || 0} transaction(s) détectée(s)`);
    } catch (e) { toast.error("Erreur analyse du relevé"); }
    finally { setIsAnalyzing(false); }
  };

  const handleConfirmImport = async (transactions) => {
    let count = 0;
    for (const txn of transactions) {
      try {
        await axios.post(`${API_URL}/api/compta/transactions`, {
          date: txn.date, amount: txn.amount, payment_method: txn.payment_method,
          notes: txn.description || null, tire_ids: [],
        }, { withCredentials: true });
        count++;
      } catch (e) { /* skip failed */ }
    }
    toast.success(`${count} transaction(s) importée(s)`);
    setImportResults(null);
    setShowImport(false);
    fetchSummary();
  };

  const methodLabel = (m) => PAYMENT_METHODS.find(p => p.value === m)?.label || m;

  return (
    <div className="space-y-4" data-testid="bilans-mensuels">
      {/* Month Navigator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} className="h-8 w-8 p-0" data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-bold text-lg text-[#0A0A0A] min-w-[180px] text-center" data-testid="current-month">
            {MONTH_NAMES[selectedMonth.month]} {selectedMonth.year}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} className="h-8 w-8 p-0" data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} className="h-8" data-testid="import-statement-btn">
            <Upload className="w-4 h-4 mr-1" /> Import relevé
          </Button>
          <Button className="h-8 bg-[#2563EB] text-white hover:bg-[#1D4ED8]" onClick={() => setShowAddTxn(true)} data-testid="add-txn-btn">
            <Plus className="w-4 h-4 mr-1" /> Transaction
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white border border-[#DBEAFE] rounded-md p-3 text-center">
            <div className="text-xs text-[#A3A3A3] uppercase">Espèces</div>
            <div className="font-mono font-bold text-lg" data-testid="total-especes">{summary.totals_by_method.especes.toFixed(2)}€</div>
          </div>
          <div className="bg-white border border-[#DBEAFE] rounded-md p-3 text-center">
            <div className="text-xs text-[#A3A3A3] uppercase">Virement</div>
            <div className="font-mono font-bold text-lg" data-testid="total-virement">{summary.totals_by_method.virement.toFixed(2)}€</div>
          </div>
          <div className="bg-white border border-[#DBEAFE] rounded-md p-3 text-center">
            <div className="text-xs text-[#A3A3A3] uppercase">CB</div>
            <div className="font-mono font-bold text-lg" data-testid="total-cb">{summary.totals_by_method.cb.toFixed(2)}€</div>
          </div>
          <div className="bg-white border border-[#DBEAFE] rounded-md p-3 text-center">
            <div className="text-xs text-[#A3A3A3] uppercase">Autre</div>
            <div className="font-mono font-bold text-lg" data-testid="total-autre">{summary.totals_by_method.autre.toFixed(2)}€</div>
          </div>
          <div className="bg-[#2563EB] text-white rounded-md p-3 text-center">
            <div className="text-xs uppercase opacity-80">Total mois</div>
            <div className="font-mono font-bold text-lg" data-testid="grand-total">{summary.grand_total.toFixed(2)}€</div>
          </div>
        </div>
      )}

      {/* Transaction List */}
      <div className="bg-white border border-[#DBEAFE] rounded-md shadow-sm">
        <div className="px-3 py-2 border-b border-[#DBEAFE] bg-[#F0F4FF] flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">
            {summary?.transaction_count || 0} transaction(s)
          </span>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-[#A3A3A3]">Chargement...</div>
        ) : !summary?.transactions?.length ? (
          <div className="p-8 text-center text-[#A3A3A3]" data-testid="no-transactions">
            Aucune transaction pour {MONTH_NAMES[selectedMonth.month]} {selectedMonth.year}
          </div>
        ) : (
          summary.transactions.map(txn => (
            <div key={txn.id} className="border-b border-[#E5E7EB] last:border-b-0 px-3 py-2 hover:bg-[#F3F4F6] text-sm" data-testid={`txn-row-${txn.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#A3A3A3] font-mono w-[80px]">{txn.date}</span>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-sm ${
                    txn.payment_method === 'cb' ? 'bg-[#DBEAFE] text-[#2563EB]' :
                    txn.payment_method === 'especes' ? 'bg-[#D1FAE5] text-[#16A34A]' :
                    txn.payment_method === 'virement' ? 'bg-[#FEF3C7] text-[#854D0E]' :
                    'bg-[#F3F4F6] text-[#525252]'
                  }`}>{methodLabel(txn.payment_method)}</span>
                  {txn.notes && <span className="text-[#525252] truncate max-w-[200px]">{txn.notes}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {txn.tire_details?.length > 0 && (
                    <span className="text-xs text-[#2563EB]">
                      <Link2 className="w-3 h-3 inline mr-0.5" />{txn.tire_details.length} pneu(s)
                    </span>
                  )}
                  <span className={`font-mono font-bold ${txn.amount >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                    {txn.amount >= 0 ? '+' : ''}{txn.amount.toFixed(2)}€
                  </span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setLinkingTxn(txn)} title="Associer des pneus"><Link2 className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingTxn(txn)}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#DC2626]" onClick={() => handleDeleteTxn(txn.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
              {/* Show linked tires */}
              {txn.tire_details?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {txn.tire_details.map(t => (
                    <span key={t.id} className="text-[10px] bg-[#F0F4FF] border border-[#DBEAFE] rounded px-1.5 py-0.5 font-mono">
                      {t.width}/{t.height}R{t.diameter} {t.brand}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddTxn} onOpenChange={setShowAddTxn}>
        <DialogContent className="sm:max-w-md" data-testid="add-txn-dialog">
          <DialogHeader><DialogTitle>Ajouter une transaction</DialogTitle><DialogDescription className="sr-only">Formulaire transaction</DialogDescription></DialogHeader>
          <TransactionForm onSubmit={handleAddTxn} onCancel={() => setShowAddTxn(false)} defaultMonth={selectedMonth} />
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editingTxn} onOpenChange={o => !o && setEditingTxn(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-txn-dialog">
          <DialogHeader><DialogTitle>Modifier la transaction</DialogTitle><DialogDescription className="sr-only">Modifier transaction</DialogDescription></DialogHeader>
          {editingTxn && <TransactionForm txn={editingTxn} onSubmit={(data) => handleUpdateTxn(editingTxn.id, data)} onCancel={() => setEditingTxn(null)} />}
        </DialogContent>
      </Dialog>

      {/* Link Tires Dialog */}
      <Dialog open={!!linkingTxn} onOpenChange={o => !o && setLinkingTxn(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" data-testid="link-tires-dialog">
          <DialogHeader><DialogTitle>Associer des pneus à la transaction</DialogTitle><DialogDescription className="sr-only">Sélection pneus</DialogDescription></DialogHeader>
          {linkingTxn && <TireLinkSelector tires={comptaTires} currentIds={linkingTxn.tire_ids || []} onConfirm={(ids) => handleLinkTires(linkingTxn.id, ids)} onCancel={() => setLinkingTxn(null)} />}
        </DialogContent>
      </Dialog>

      {/* Import Statement Dialog */}
      <Dialog open={showImport} onOpenChange={o => { if (!o) { setShowImport(false); setImportResults(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" data-testid="import-statement-dialog">
          <DialogHeader><DialogTitle>Importer un relevé bancaire</DialogTitle><DialogDescription className="sr-only">Import relevé</DialogDescription></DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 p-1">
            {!importResults ? (
              <StatementImporter onImport={handleImportStatement} isAnalyzing={isAnalyzing} />
            ) : (
              <ImportReview results={importResults} onConfirm={handleConfirmImport} onCancel={() => setImportResults(null)} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Transaction Form
const TransactionForm = ({ txn, onSubmit, onCancel, defaultMonth }) => {
  const [date, setDate] = useState(txn?.date || (defaultMonth ? `${defaultMonth.year}-${String(defaultMonth.month).padStart(2,'0')}-01` : new Date().toISOString().slice(0,10)));
  const [amount, setAmount] = useState(txn?.amount?.toString() || "");
  const [method, setMethod] = useState(txn?.payment_method || "cb");
  const [notes, setNotes] = useState(txn?.notes || "");
  return (
    <div className="space-y-4 p-4" data-testid="txn-form">
      <div><Label className="input-label">Date *</Label><Input type="date" className="input-field" value={date} onChange={e => setDate(e.target.value)} data-testid="txn-date" /></div>
      <div><Label className="input-label">Montant (€) *</Label><Input type="number" step="0.01" className="input-field" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" data-testid="txn-amount" /></div>
      <div><Label className="input-label">Moyen de paiement</Label>
        <Select value={method} onValueChange={setMethod}><SelectTrigger className="input-field" data-testid="txn-method"><SelectValue /></SelectTrigger>
          <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="input-label">Notes</Label><Input className="input-field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Description..." data-testid="txn-notes" /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="h-9">Annuler</Button>
        <Button className="btn-primary h-9" data-testid="txn-submit" onClick={() => {
          if (!date || !amount) { toast.error("Date et montant requis"); return; }
          onSubmit({ date, amount: parseFloat(amount), payment_method: method, notes: notes || null });
        }}>{txn ? "Modifier" : "Ajouter"}</Button>
      </div>
    </div>
  );
};

// Tire Link Selector
const TireLinkSelector = ({ tires, currentIds, onConfirm, onCancel }) => {
  const [selected, setSelected] = useState(new Set(currentIds));
  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  return (
    <div className="p-2">
      <div className="max-h-[50vh] overflow-y-auto space-y-1">
        {tires.length === 0 ? (
          <div className="text-center text-[#A3A3A3] py-4">Aucun pneu dans Compta Stock</div>
        ) : tires.map(t => (
          <label key={t.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm hover:bg-[#F0F4FF] ${selected.has(t.id) ? 'bg-[#DBEAFE]' : ''}`}>
            <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggle(t.id)} />
            <span className="font-mono text-xs font-bold">{t.width}/{t.height}R{t.diameter}</span>
            <span className="text-[#525252]">{t.brand} {t.model}</span>
            {t.purchase_value != null && <span className="text-xs text-[#A3A3A3] ml-auto">{t.purchase_value.toFixed(2)}€</span>}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t mt-3">
        <Button variant="outline" onClick={onCancel} className="h-9">Annuler</Button>
        <Button className="btn-primary h-9" onClick={() => onConfirm([...selected])} data-testid="confirm-link-btn">
          Associer ({selected.size})
        </Button>
      </div>
    </div>
  );
};

// Statement Importer
const StatementImporter = ({ onImport, isAnalyzing }) => {
  const fileRef = useRef(null);
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onImport(file);
  };
  return (
    <div className="text-center py-6 space-y-4" data-testid="statement-importer">
      <div className="text-sm text-[#525252]">
        Importez un relevé bancaire (PDF ou image).<br />
        L'IA analysera le document et extraira les transactions automatiquement.
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleFile} />
      <Button onClick={() => fileRef.current?.click()} disabled={isAnalyzing} className="h-10 bg-[#2563EB] text-white hover:bg-[#1D4ED8]" data-testid="upload-statement-btn">
        {isAnalyzing ? (
          <span className="flex items-center gap-2"><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Analyse en cours...</span>
        ) : (
          <><Upload className="w-4 h-4 mr-2" /> Sélectionner un fichier</>
        )}
      </Button>
      <div className="text-xs text-[#A3A3A3]">Formats acceptés : PDF, PNG, JPG, WEBP</div>
    </div>
  );
};

// Import Review
const ImportReview = ({ results, onConfirm, onCancel }) => {
  const [txns, setTxns] = useState(results.transactions || []);
  const updateTxn = (idx, field, value) => {
    setTxns(prev => prev.map((t, i) => i === idx ? {...t, [field]: value} : t));
  };
  const removeTxn = (idx) => setTxns(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="import-review">
      <div className="text-sm text-[#525252]">{txns.length} transaction(s) détectée(s). Vérifiez et corrigez avant de valider.</div>
      <div className="max-h-[40vh] overflow-y-auto space-y-2">
        {txns.map((txn, idx) => (
          <div key={idx} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded p-2 text-sm">
            <div className="flex items-center gap-2">
              <Input type="date" className="input-field h-7 text-xs w-[130px]" value={txn.date} onChange={e => updateTxn(idx, "date", e.target.value)} />
              <Input type="number" step="0.01" className="input-field h-7 text-xs w-[100px]" value={txn.amount} onChange={e => updateTxn(idx, "amount", parseFloat(e.target.value))} />
              <Select value={txn.payment_method} onValueChange={v => updateTxn(idx, "payment_method", v)}>
                <SelectTrigger className="h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-[#525252] truncate flex-1">{txn.description}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#DC2626]" onClick={() => removeTxn(idx)}><X className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="h-9">Annuler</Button>
        <Button className="btn-primary h-9" onClick={() => onConfirm(txns)} disabled={txns.length === 0} data-testid="confirm-import-btn">
          Valider {txns.length} transaction(s)
        </Button>
      </div>
    </div>
  );
};

// ==========================================
// TAB 3 — HISTORIQUE ANNUEL
// ==========================================
const HistoriqueAnnuel = () => {
  const [startYear, setStartYear] = useState(2025);
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [snapshotDate, setSnapshotDate] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [showSnapshot, setShowSnapshot] = useState(false);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/compta/annual-history`, {
        params: { start_year: startYear }, withCredentials: true,
      });
      setHistory(res.data);
    } catch (e) { toast.error("Erreur chargement historique"); }
    finally { setIsLoading(false); }
  }, [startYear]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSnapshot = async () => {
    if (!snapshotDate) { toast.error("Sélectionnez une date"); return; }
    try {
      const res = await axios.get(`${API_URL}/api/compta/snapshot`, {
        params: { date: snapshotDate }, withCredentials: true,
      });
      setSnapshot(res.data);
      setShowSnapshot(true);
    } catch (e) { toast.error("Erreur snapshot"); }
  };

  return (
    <div className="space-y-4" data-testid="historique-annuel">
      {/* Year Navigator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setStartYear(y => y - 1)} className="h-8 w-8 p-0" data-testid="prev-year"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-bold text-lg text-[#0A0A0A] min-w-[220px] text-center" data-testid="current-year">
            {history?.accounting_year || `Nov ${startYear} — Oct ${startYear + 1}`}
          </span>
          <Button variant="outline" size="sm" onClick={() => setStartYear(y => y + 1)} className="h-8 w-8 p-0" data-testid="next-year"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" className="input-field h-8 text-sm w-[160px]" value={snapshotDate} onChange={e => setSnapshotDate(e.target.value)} data-testid="snapshot-date" />
          <Button variant="outline" size="sm" onClick={handleSnapshot} className="h-8" data-testid="snapshot-btn">
            <Eye className="w-4 h-4 mr-1" /> Snapshot
          </Button>
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-white border border-[#DBEAFE] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F0F4FF] border-b border-[#DBEAFE]">
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Mois</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#16A34A]">Entrées</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#16A34A] hidden sm:table-cell">Val. entrées</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#DC2626]">Sorties</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#DC2626] hidden sm:table-cell">Val. sorties</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#2563EB] hidden sm:table-cell">Transactions</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#0A0A0A]">Inventaire</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#A3A3A3]">Chargement...</td></tr>
            ) : history?.months?.map((m, idx) => (
              <tr key={idx} className={`border-b border-[#E5E7EB] hover:bg-[#F3F4F6] ${m.entries_count === 0 && m.exits_count === 0 ? 'opacity-50' : ''}`} data-testid={`history-row-${m.year}-${m.month}`}>
                <td className="px-3 py-2 font-medium">{m.month_name} {m.year}</td>
                <td className="px-3 py-2 text-right font-mono text-[#16A34A]">{m.entries_count > 0 ? `+${m.entries_count}` : '—'}</td>
                <td className="px-3 py-2 text-right font-mono text-[#16A34A] hidden sm:table-cell">{m.entries_value > 0 ? `${m.entries_value.toFixed(0)}€` : '—'}</td>
                <td className="px-3 py-2 text-right font-mono text-[#DC2626]">{m.exits_count > 0 ? `-${m.exits_count}` : '—'}</td>
                <td className="px-3 py-2 text-right font-mono text-[#DC2626] hidden sm:table-cell">{m.exits_value > 0 ? `${m.exits_value.toFixed(0)}€` : '—'}</td>
                <td className="px-3 py-2 text-right font-mono text-[#2563EB] hidden sm:table-cell">{m.transaction_total !== 0 ? `${m.transaction_total.toFixed(0)}€` : '—'}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{m.running_inventory}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Snapshot Dialog */}
      <Dialog open={showSnapshot} onOpenChange={setShowSnapshot}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" data-testid="snapshot-dialog">
          <DialogHeader>
            <DialogTitle>Snapshot au {snapshot?.date}</DialogTitle>
            <DialogDescription className="sr-only">Inventaire historique</DialogDescription>
          </DialogHeader>
          {snapshot && (
            <div className="overflow-y-auto flex-1 min-h-0 p-2">
              <div className="flex items-center gap-4 mb-3 text-sm">
                <span className="font-bold">{snapshot.total_quantity} pneu(s)</span>
                <span className="text-[#2563EB] font-mono">{snapshot.total_value.toFixed(2)}€ valeur achat</span>
              </div>
              {snapshot.tires.length === 0 ? (
                <div className="text-center text-[#A3A3A3] py-4">Aucun pneu à cette date</div>
              ) : (
                <div className="space-y-1">
                  {snapshot.tires.map(t => (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-[#F9FAFB] rounded p-2">
                      <span className="font-mono font-bold">{t.width}/{t.height}R{t.diameter} — {t.brand} {t.model}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[#A3A3A3]">x{t.quantity}</span>
                        {t.purchase_value != null && <span className="font-mono">{t.purchase_value.toFixed(2)}€</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// TAB 4 — COMPARAISON RECO STOCK / COMPTA STOCK
// ==========================================
const PIE_COLORS = ["#16A34A", "#EAB308", "#DC2626", "#2563EB", "#7C3AED", "#EC4899"];
const BAR_RECO = "#0A0A0A";
const BAR_COMPTA = "#2563EB";

const Comparaison = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all"); // all, reco_only, compta_only, both, mismatch

  const fetchComparison = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/compta/comparison`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error("Erreur chargement comparaison"); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  const filteredTable = useMemo(() => {
    if (!data) return [];
    if (filterStatus === "all") return data.table;
    if (filterStatus === "mismatch") return data.table.filter(r => !r.qty_match);
    return data.table.filter(r => r.status === filterStatus);
  }, [data, filterStatus]);

  if (isLoading) return <div className="p-8 text-center text-[#A3A3A3]">Chargement...</div>;
  if (!data) return null;

  const { indicators: ind, charts } = data;

  return (
    <div className="space-y-4" data-testid="comparaison-tab">
      {/* Indicators */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E7EB] rounded-md p-3 text-center">
          <div className="text-xs text-[#A3A3A3] uppercase">Reco Stock</div>
          <div className="font-mono font-bold text-2xl text-[#0A0A0A]" data-testid="ind-reco">{ind.reco_total}</div>
          <div className="text-xs text-[#A3A3A3]">pneus</div>
        </div>
        <div className="bg-white border border-[#DBEAFE] rounded-md p-3 text-center">
          <div className="text-xs text-[#A3A3A3] uppercase">Compta Stock</div>
          <div className="font-mono font-bold text-2xl text-[#2563EB]" data-testid="ind-compta">{ind.compta_total}</div>
          <div className="text-xs text-[#A3A3A3]">pneus — {ind.compta_purchase_value.toFixed(0)}€</div>
        </div>
        <div className="bg-white border border-[#D1FAE5] rounded-md p-3 text-center">
          <div className="text-xs text-[#A3A3A3] uppercase">En commun</div>
          <div className="font-mono font-bold text-2xl text-[#16A34A]" data-testid="ind-both">{ind.in_both}</div>
          <div className="text-xs text-[#A3A3A3]">dimensions</div>
        </div>
        <div className="bg-white border border-[#FEF3C7] rounded-md p-3 text-center">
          <div className="text-xs text-[#A3A3A3] uppercase">Écarts</div>
          <div className="font-mono font-bold text-2xl text-[#854D0E]" data-testid="ind-mismatches">{ind.qty_mismatches + ind.only_reco + ind.only_compta}</div>
          <div className="text-xs text-[#A3A3A3]">{ind.only_reco} Reco seul · {ind.only_compta} Compta seul</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Season Chart */}
        <div className="bg-white border border-[#E5E7EB] rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-3">Par saison</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={charts.season}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
              <Tooltip /><Legend />
              <Bar dataKey="reco" name="Reco Stock" fill={BAR_RECO} radius={[2,2,0,0]} />
              <Bar dataKey="compta" name="Compta Stock" fill={BAR_COMPTA} radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Brand Chart */}
        <div className="bg-white border border-[#E5E7EB] rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-3">Par marque (Top 10)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={charts.brand} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              <Bar dataKey="reco" name="Reco" fill={BAR_RECO} radius={[0,2,2,0]} />
              <Bar dataKey="compta" name="Compta" fill={BAR_COMPTA} radius={[0,2,2,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden">
        <div className="px-3 py-2 border-b border-[#E5E7EB] bg-[#F0F4FF] flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">
            {filteredTable.length} dimension(s)
          </span>
          <div className="flex items-center gap-1">
            {[
              { v: "all", l: "Toutes" },
              { v: "both", l: "En commun" },
              { v: "reco_only", l: "Reco seul" },
              { v: "compta_only", l: "Compta seul" },
              { v: "mismatch", l: "Écarts qty" },
            ].map(f => (
              <button key={f.v} onClick={() => setFilterStatus(f.v)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-all ${filterStatus === f.v ? 'bg-[#2563EB] text-white border-transparent' : 'bg-white text-[#525252] border-[#E5E7EB]'}`}
                data-testid={`comp-filter-${f.v}`}>
                {f.l}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-[#525252]">Dimension</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-[#0A0A0A]">Reco</th>
              <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-[#2563EB]">Compta</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-[#525252] hidden sm:table-cell">Marques Reco</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-[#525252] hidden sm:table-cell">Marques Compta</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase text-[#525252]">Statut</th>
            </tr>
          </thead>
          <tbody>
            {filteredTable.map(row => (
              <tr key={row.dimension} className={`border-b border-[#E5E7EB] hover:bg-[#F3F4F6] ${
                row.status === 'reco_only' ? 'bg-[#FFF7ED]' : row.status === 'compta_only' ? 'bg-[#EFF6FF]' : !row.qty_match ? 'bg-[#FFFBEB]' : ''
              }`} data-testid={`comp-row-${row.dimension}`}>
                <td className="px-3 py-1.5 font-mono font-bold text-xs">{row.dimension}</td>
                <td className="px-3 py-1.5 text-right font-mono">{row.reco_qty || '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono text-[#2563EB]">{row.compta_qty || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-[#525252] hidden sm:table-cell truncate max-w-[150px]">{row.reco_brands.join(", ") || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-[#525252] hidden sm:table-cell truncate max-w-[150px]">{row.compta_brands.join(", ") || '—'}</td>
                <td className="px-3 py-1.5 text-center">
                  {row.status === 'both' && row.qty_match && <span className="text-xs text-[#16A34A] font-bold">OK</span>}
                  {row.status === 'both' && !row.qty_match && <span className="text-xs text-[#854D0E] font-bold">Écart</span>}
                  {row.status === 'reco_only' && <span className="text-xs text-[#EA580C] font-bold">Reco seul</span>}
                  {row.status === 'compta_only' && <span className="text-xs text-[#2563EB] font-bold">Compta seul</span>}
                </td>
              </tr>
            ))}
            {filteredTable.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[#A3A3A3]">Aucune dimension pour ce filtre</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==========================================
// INVOICE ANALYZER
// ==========================================
const InvoiceAnalyzer = ({ onClose, onTiresAdded }) => {
  const [step, setStep] = useState("upload"); // upload, review, reco_proposal
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [extractedTires, setExtractedTires] = useState([]);
  const [recoProposals, setRecoProposals] = useState([]);
  const [addingToCompta, setAddingToCompta] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/compta/analyze-invoice`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      if (res.data.error) {
        toast.error(res.data.error);
        if (res.data.traceback) console.error("[INVOICE TRACEBACK]", res.data.traceback);
        return;
      }
      const tires = (res.data.tires || []).map((t, i) => ({
        ...t, _idx: i, _include: true,
        tire_type: "new", status: "en_stock", condition_score: null, dot: null, is_tested: true,
      }));
      setExtractedTires(tires);
      setStep("review");
      toast.success(`${tires.length} pneu(s) détecté(s)`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Erreur inconnue";
      toast.error(`Erreur analyse facture: ${detail}`);
      console.error("[INVOICE ERROR]", e.response?.data || e);
    } finally { setIsAnalyzing(false); }
  };

  const updateTire = (idx, field, value) => {
    setExtractedTires(prev => prev.map(t => t._idx === idx ? {...t, [field]: value} : t));
  };

  const handleConfirmCompta = async () => {
    const included = extractedTires.filter(t => t._include);
    if (included.length === 0) { toast.error("Sélectionnez au moins un pneu"); return; }
    setAddingToCompta(true);
    let count = 0;
    const proposals = [];
    for (const t of included) {
      try {
        await axios.post(`${API_URL}/api/compta/tires`, {
          width: t.width, height: t.height, diameter: t.diameter,
          load_index: t.load_index || null, speed_index: t.speed_index || null,
          brand: t.brand, model: t.model || "-", tire_type: t.tire_type,
          quantity: t.quantity || 1, season: t.season || "ete",
          purchase_value: t.unit_price || null, resale_value: null,
          status: "en_stock", is_tested: t.is_tested,
          runflat: t.runflat || false,
          condition_score: t.condition_score ? parseInt(t.condition_score) : null,
        }, { withCredentials: true });
        count++;
        proposals.push({...t, _accepted: null}); // null = pending decision
      } catch (e) { /* skip */ }
    }
    toast.success(`${count} pneu(s) ajouté(s) à Compta Stock`);
    setRecoProposals(proposals);
    setStep("reco_proposal");
    setAddingToCompta(false);
    if (onTiresAdded) onTiresAdded();
  };

  const handleRecoDecision = async (idx, accept) => {
    setRecoProposals(prev => prev.map((t, i) => i === idx ? {...t, _accepted: accept} : t));
    if (accept) {
      const t = recoProposals[idx];
      try {
        await axios.post(`${API_URL}/api/compta/add-to-reco`, {
          width: t.width, height: t.height, diameter: t.diameter,
          load_index: t.load_index || null, speed_index: t.speed_index || null,
          brand: t.brand, model: t.model || "-", tire_type: t.tire_type,
          quantity: t.quantity || 1, season: t.season || "ete",
          is_tested: t.is_tested, condition_score: t.condition_score ? parseInt(t.condition_score) : null,
        }, { withCredentials: true });
        toast.success(`${t.brand} ${t.width}/${t.height}R${t.diameter} ajouté à Reco Stock`);
      } catch (e) { toast.error("Erreur ajout Reco Stock"); }
    }
  };

  const allDecided = recoProposals.length > 0 && recoProposals.every(t => t._accepted !== null);

  return (
    <div className="space-y-4" data-testid="invoice-analyzer">
      {step === "upload" && (
        <div className="text-center py-8 space-y-4">
          <FileUp className="w-12 h-12 text-[#2563EB] mx-auto" />
          <div className="text-sm text-[#525252]">
            Importez une facture d'achat (PDF ou image).<br />
            L'IA extraira automatiquement les pneus et leurs prix.
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleUpload} />
          <Button onClick={() => fileRef.current?.click()} disabled={isAnalyzing} className="h-10 bg-[#2563EB] text-white hover:bg-[#1D4ED8]" data-testid="upload-invoice-btn">
            {isAnalyzing ? <span className="flex items-center gap-2"><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Analyse en cours...</span>
            : <><Upload className="w-4 h-4 mr-2" /> Sélectionner une facture</>}
          </Button>
          <div className="text-xs text-[#A3A3A3]">PDF, PNG, JPG, WEBP</div>
        </div>
      )}

      {step === "review" && (
        <div data-testid="invoice-review">
          <div className="text-sm text-[#525252] mb-3">{extractedTires.length} pneu(s) détecté(s). Vérifiez et corrigez avant de valider.</div>
          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {extractedTires.map(t => (
              <div key={t._idx} className={`border rounded p-2 text-sm ${t._include ? 'border-[#DBEAFE] bg-white' : 'border-[#E5E7EB] bg-[#F3F4F6] opacity-60'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Checkbox checked={t._include} onCheckedChange={v => updateTire(t._idx, "_include", v)} />
                  <Input className="input-field h-7 text-xs w-[60px]" type="number" value={t.width} onChange={e => updateTire(t._idx, "width", parseInt(e.target.value))} />
                  <span className="text-[#A3A3A3]">/</span>
                  <Input className="input-field h-7 text-xs w-[50px]" type="number" value={t.height} onChange={e => updateTire(t._idx, "height", parseInt(e.target.value))} />
                  <span className="text-[#A3A3A3]">R</span>
                  <Input className="input-field h-7 text-xs w-[50px]" type="number" value={t.diameter} onChange={e => updateTire(t._idx, "diameter", parseInt(e.target.value))} />
                  <Input className="input-field h-7 text-xs w-[90px]" value={t.brand} onChange={e => updateTire(t._idx, "brand", e.target.value)} placeholder="Marque" />
                  <Input className="input-field h-7 text-xs w-[90px]" value={t.model || ""} onChange={e => updateTire(t._idx, "model", e.target.value)} placeholder="Modèle" />
                  <span className="text-[#A3A3A3] text-xs">x</span>
                  <Input className="input-field h-7 text-xs w-[40px]" type="number" value={t.quantity} onChange={e => updateTire(t._idx, "quantity", parseInt(e.target.value))} />
                  <Input className="input-field h-7 text-xs w-[70px]" type="number" step="0.01" value={t.unit_price} onChange={e => updateTire(t._idx, "unit_price", parseFloat(e.target.value))} placeholder="Prix €" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => { setStep("upload"); setExtractedTires([]); }} className="h-9">Annuler</Button>
            <Button className="btn-primary h-9" onClick={handleConfirmCompta} disabled={addingToCompta} data-testid="confirm-compta-btn">
              {addingToCompta ? "Ajout en cours..." : `Ajouter ${extractedTires.filter(t => t._include).length} pneu(s) à Compta`}
            </Button>
          </div>
        </div>
      )}

      {step === "reco_proposal" && (
        <div data-testid="reco-proposal">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#EAB308]" />
            <span className="text-sm font-medium">Propositions pour Reco Stock — Acceptez ou refusez chaque pneu individuellement</span>
          </div>
          <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {recoProposals.map((t, idx) => (
              <div key={idx} className={`flex items-center justify-between border rounded p-2 text-sm ${
                t._accepted === true ? 'border-[#16A34A] bg-[#F0FDF4]' :
                t._accepted === false ? 'border-[#DC2626] bg-[#FEF2F2] opacity-60' :
                'border-[#DBEAFE] bg-white'
              }`} data-testid={`reco-proposal-${idx}`}>
                <span className="font-mono text-xs font-bold">{t.width}/{t.height}R{t.diameter} — {t.brand} {t.model || "-"} x{t.quantity}</span>
                {t._accepted === null ? (
                  <div className="flex items-center gap-1">
                    <Button size="sm" className="h-7 text-xs bg-[#16A34A] text-white hover:bg-[#15803D]" onClick={() => handleRecoDecision(idx, true)} data-testid={`accept-reco-${idx}`}>
                      <Check className="w-3 h-3 mr-1" /> Accepter
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-[#DC2626] hover:bg-[#FEF2F2]" onClick={() => handleRecoDecision(idx, false)} data-testid={`reject-reco-${idx}`}>
                      <X className="w-3 h-3 mr-1" /> Refuser
                    </Button>
                  </div>
                ) : (
                  <span className={`text-xs font-bold ${t._accepted ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                    {t._accepted ? "Ajouté à Reco Stock" : "Refusé"}
                  </span>
                )}
              </div>
            ))}
          </div>
          {allDecided && (
            <div className="flex justify-end pt-3">
              <Button className="btn-primary h-9" onClick={onClose} data-testid="finish-invoice-btn">Terminé</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================
const ComptaStockPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("stock");

  // Tab 1 state (Stock en cours)
  const [tires, setTires] = useState([]);
  const [stats, setStats] = useState({
    en_stock: { count: 0, purchase_total: 0, resale_total: 0 },
    en_vente: { count: 0, purchase_total: 0, resale_total: 0 },
    vendu: { count: 0, purchase_total: 0, resale_total: 0 },
    total_tires: 0, total_purchase_value: 0, total_resale_value: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTire, setEditingTire] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [showInvoiceAnalyzer, setShowInvoiceAnalyzer] = useState(false);
  const [statusFilters, setStatusFilters] = useState({ en_stock: true, en_vente: true, vendu: false });
  const [searchText, setSearchText] = useState("");

  const activeStatusFilters = useMemo(() => Object.entries(statusFilters).filter(([, v]) => v).map(([k]) => k).join(","), [statusFilters]);

  const fetchTires = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (activeStatusFilters) params.status = activeStatusFilters;
      const res = await axios.get(`${API_URL}/api/compta/tires/grouped`, { params, withCredentials: true });
      setTires(res.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setIsLoading(false); }
  }, [activeStatusFilters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/compta/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (activeTab === "stock") { fetchTires(); } fetchStats(); }, [activeTab, fetchTires, fetchStats]);

  const refreshData = () => { fetchTires(); fetchStats(); };

  const filteredTires = useMemo(() => {
    if (!searchText) return tires;
    const q = searchText.toLowerCase();
    return tires.filter(t => t.brand?.toLowerCase().includes(q) || t.model?.toLowerCase().includes(q) || t.dimensions?.toLowerCase().includes(q));
  }, [tires, searchText]);

  const regularTires = useMemo(() => filteredTires.filter(t => !t.camionnette), [filteredTires]);
  const camionnetteTires = useMemo(() => filteredTires.filter(t => t.camionnette), [filteredTires]);

  const handleStatusChange = async (tireId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/compta/tires/${tireId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Statut changé : ${STATUS_CONFIG[newStatus]?.label}`);
      refreshData();
    } catch (e) { toast.error("Erreur changement statut"); }
  };

  const handleDelete = async (tireId) => {
    if (!window.confirm("Supprimer ce pneu de Compta Stock ?")) return;
    try {
      await axios.delete(`${API_URL}/api/compta/tires/${tireId}`, { withCredentials: true });
      toast.success("Pneu supprimé");
      refreshData();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const TABS = [
    { id: "stock", label: "Stock en cours" },
    { id: "bilans", label: "Bilans mensuels" },
    { id: "historique", label: "Historique annuel" },
    { id: "comparaison", label: "Comparaison" },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4FF]" data-testid="compta-stock-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-[#2563EB] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#2563EB] rounded-sm flex items-center justify-center"><BookOpen className="w-5 h-5 text-white" /></div>
              <span className="text-lg font-bold text-[#0A0A0A] hidden sm:block">Compta Stock</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate("/")} className="h-8 px-3 border-[#0A0A0A]" data-testid="reco-stock-btn"><Package className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Reco Stock</span></Button>
              <Button variant="outline" size="sm" onClick={() => setShowInvoiceAnalyzer(true)} className="h-8 px-3 border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF]" data-testid="invoice-analyzer-btn"><FileUp className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Facture</span></Button>
              {activeTab === "stock" && (
                <Button className="h-8 px-3 bg-[#2563EB] text-white hover:bg-[#1D4ED8]" onClick={() => setShowAddForm(true)} data-testid="compta-add-tire-btn"><Plus className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Ajouter</span></Button>
              )}
              <span className="text-sm text-[#525252] hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout} className="text-[#525252] hover:text-[#0A0A0A] h-8 w-8 p-0" data-testid="compta-logout-btn"><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-[#DBEAFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-center gap-4 sm:gap-6 text-sm flex-wrap">
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#16A34A]" /><span className="font-mono font-bold" data-testid="compta-stat-en-stock">{stats.en_stock.count}</span><span className="text-[#A3A3A3]">en stock</span></div>
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EAB308]" /><span className="font-mono font-bold" data-testid="compta-stat-en-vente">{stats.en_vente.count}</span><span className="text-[#A3A3A3]">en vente</span></div>
            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#DC2626]" /><span className="font-mono font-bold" data-testid="compta-stat-vendu">{stats.vendu.count}</span><span className="text-[#A3A3A3]">vendus</span></div>
            <div className="hidden sm:flex items-center gap-1 border-l border-[#E5E7EB] pl-4"><span className="font-mono font-bold text-[#2563EB]" data-testid="compta-stat-purchase">{stats.total_purchase_value.toFixed(0)}€</span><span className="text-[#A3A3A3]">achat</span></div>
            <div className="hidden sm:flex items-center gap-1"><span className="font-mono font-bold text-[#16A34A]" data-testid="compta-stat-resale">{stats.total_resale_value.toFixed(0)}€</span><span className="text-[#A3A3A3]">revente</span></div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[#DBEAFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-none -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`tab-trigger ${activeTab === tab.id ? 'tab-trigger-active border-[#2563EB] text-[#2563EB]' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`compta-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

        {/* TAB 1: Stock en cours */}
        {activeTab === "stock" && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#A3A3A3]" />
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button key={key} onClick={() => setStatusFilters(p => ({...p, [key]: !p[key]}))} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${statusFilters[key] ? `${cfg.color} ${cfg.text} border-transparent` : 'bg-white text-[#525252] border-[#E5E7EB]'}`} data-testid={`compta-filter-${key}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusFilters[key] ? 'bg-white' : cfg.dot}`} />{cfg.label}
                  </button>
                ))}
              </div>
              <div className="flex-1 min-w-[150px] max-w-xs">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" />
                  <Input className="input-field pl-8 h-8 text-sm" placeholder="Rechercher marque, modèle..." value={searchText} onChange={e => setSearchText(e.target.value)} data-testid="compta-search-input" />
                  {searchText && <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}><X className="w-3.5 h-3.5 text-[#A3A3A3]" /></button>}
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#DBEAFE] rounded-md shadow-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#DBEAFE] bg-[#F0F4FF]">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">{filteredTires.length} pneu{filteredTires.length !== 1 ? 's' : ''}</span>
              </div>
              {isLoading ? <div className="p-8 text-center text-[#A3A3A3]">Chargement...</div>
              : filteredTires.length === 0 ? <div className="p-8 text-center text-[#A3A3A3]" data-testid="compta-empty-state">{tires.length === 0 ? "Aucun pneu dans Compta Stock." : "Aucun résultat."}</div>
              : <>
                  {regularTires.map(g => <ComptaTireRow key={g.group_key} group={g} onEdit={setEditingTire} onDelete={handleDelete} onStatusChange={handleStatusChange} expandedKey={expandedKey} onExpand={setExpandedKey} />)}
                  {camionnetteTires.length > 0 && <>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F0F4FF] border-t border-b border-[#DBEAFE]">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB]">Camionnette</span>
                      <div className="flex-1 h-px bg-[#DBEAFE]" />
                    </div>
                    {camionnetteTires.map(g => <ComptaTireRow key={g.group_key} group={g} onEdit={setEditingTire} onDelete={handleDelete} onStatusChange={handleStatusChange} expandedKey={expandedKey} onExpand={setExpandedKey} />)}
                  </>}
                </>}
            </div>
          </>
        )}

        {/* TAB 2: Bilans mensuels */}
        {activeTab === "bilans" && <BilansMensuels />}

        {/* TAB 3: Historique annuel */}
        {activeTab === "historique" && <HistoriqueAnnuel />}

        {/* TAB 4: Comparaison */}
        {activeTab === "comparaison" && <Comparaison />}
      </main>

      {/* Add/Edit Dialogs */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] flex flex-col" data-testid="compta-add-dialog">
          <DialogHeader className="flex-shrink-0"><DialogTitle><Plus className="w-4 h-4 inline mr-1" />Ajouter un pneu — Compta Stock</DialogTitle><DialogDescription className="sr-only">Ajout pneu comptable</DialogDescription></DialogHeader>
          <div className="px-6 pt-4 pb-6 overflow-y-auto flex-1 min-h-0"><ComptaTireForm onClose={() => setShowAddForm(false)} onSuccess={refreshData} /></div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editingTire} onOpenChange={o => !o && setEditingTire(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] flex flex-col" data-testid="compta-edit-dialog">
          <DialogHeader className="flex-shrink-0"><DialogTitle><Pencil className="w-4 h-4 inline mr-1" />Modifier le pneu</DialogTitle><DialogDescription className="sr-only">Modification pneu comptable</DialogDescription></DialogHeader>
          <div className="px-6 pt-4 pb-6 overflow-y-auto flex-1 min-h-0">{editingTire && <ComptaTireForm tire={editingTire} onClose={() => setEditingTire(null)} onSuccess={refreshData} />}</div>
        </DialogContent>
      </Dialog>

      {/* Invoice Analyzer Dialog */}
      <Dialog open={showInvoiceAnalyzer} onOpenChange={setShowInvoiceAnalyzer}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] flex flex-col" data-testid="invoice-analyzer-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2"><FileUp className="w-4 h-4" /> Analyseur de factures</DialogTitle>
            <DialogDescription className="sr-only">Analyse de facture d'achat</DialogDescription>
          </DialogHeader>
          <div className="px-6 pt-2 pb-6 overflow-y-auto flex-1 min-h-0">
            <InvoiceAnalyzer onClose={() => setShowInvoiceAnalyzer(false)} onTiresAdded={refreshData} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComptaStockPage;
